import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {ONLY_ON_PRIMARY_LOCALE} from './localization';
import {WHEN_NOT_IN_OPTIMIZE_MODE} from './optimization';

export const canAccessPageSettings = defineAbilityConfig({
  workflows: {
    canvas: true,
    comment: true,
    preview: true,
    logicFlows: true,
    dynamicData: true,
    optimization: true,
    analyzeVisualView: true,
    optimizationClickGoal: true,
    optimizationRedirectTest: true,
  },
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
} as const);

export const canManagePageSettings = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {design: true},
    permission: ['manage', 'sitePageSettings'],
  },
  {
    abilities: {canAccessStaticPageContentFgac: false},
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {edit: true},
    permission: ['manage', 'sitePageSettings'],
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      canEditLocaleContent: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {design: true, edit: true},
    authorizations: {'SitePageSettings/manage': true},
  },
] as const);

/**
 * Whether or not the duplicate page button is rendered is also based on
 * the type of page - that is not represented in this ability.
 *
 * For example, static page templates cannot be duplicated in build mode.
 */
export const canDuplicatePage = defineAbilityConfig([
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      canEditLocalizedCanvasItem: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    permission: ['duplicate', 'sitePage'],
    siteModes: {design: true, edit: true},
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      canEditLocalizedCanvasItem: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    siteModes: {design: true, edit: true},
    authorizations: {'SitePage/duplicate': true},
  },
] as const);

export const canCreatePageWithTemplate = defineAbilityConfig([
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permissions: [
      ['createFromTemplate', 'sitePage'],
      ['design', 'site'],
    ],
    siteModes: {design: true},
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      isInPageBuildMode: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permissions: [
      ['createFromTemplate', 'sitePage'],
      ['design', 'site'],
    ],
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      isInPageBuildMode: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permissions: [
      ['createFromTemplate', 'sitePage'],
      ['design', 'site'],
    ],
    authorizations: {'SitePage/createFromTemplate': true},
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permissions: [
      ['createFromTemplate', 'sitePage'],
      ['design', 'site'],
    ],
    siteModes: {design: true},
    authorizations: {'SitePage/createFromTemplate': true},
  },
] as const);

export const canCreatePageWithoutTemplate = defineAbilityConfig([
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permissions: [
      ['create', 'sitePage'],
      ['design', 'site'],
    ],
    siteModes: {design: true},
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      isInPageBuildMode: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permissions: [
      ['create', 'sitePage'],
      ['design', 'site'],
    ],
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      isInPageBuildMode: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permissions: [
      ['create', 'sitePage'],
      ['design', 'site'],
    ],
    authorizations: {'SitePage/create': true},
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permissions: [
      ['create', 'sitePage'],
      ['design', 'site'],
    ],
    siteModes: {design: true},
    authorizations: {'SitePage/create': true},
  },
] as const);

export const canCreatePage = defineAbilityConfig([
  {abilities: {canCreatePageWithoutTemplate: true}},
  {abilities: {canCreatePageWithTemplate: true}},
] as const);

/**
 * Allows users in design and design branch (aka view) mode to create standalone branches without initializing them
 * from a template. Users in edit site mode, regardless whether they're Editors or Marketers, should not be able to
 * create standalone branches without a template.
 */
export const canCreateStandaloneBranchWithoutTemplate = defineAbilityConfig([
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      canAccessPageBranching: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permission: ['createStandalone', 'siteBranch'],
    siteModes: {design: true, view: true},
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      isInPageBuildMode: true,
      canAccessPageBranching: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permissions: [['createStandalone', 'siteBranch']],
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      canAccessPageBranching: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permission: ['createStandalone', 'siteBranch'],
    siteModes: {design: true, view: true},
    authorizations: {'SiteBranch/createStandalone': true},
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      isInPageBuildMode: true,
      canAccessPageBranching: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permissions: [['createStandalone', 'siteBranch']],
    authorizations: {'SiteBranch/createStandalone': true},
  },
] as const);

/**
 * Allows users in design and design branch (aka view) mode to create standalone branches from templates. Users in edit
 * site mode can only create standalone branches if they're in build mode as well.
 */
export const canCreateStandaloneBranchWithTemplate = defineAbilityConfig([
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      canAccessPageBranching: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permission: ['createFromTemplate', 'siteBranch'],
    siteModes: {design: true, view: true},
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      isInPageBuildMode: true,
      canAccessPageBranching: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permission: ['createFromTemplate', 'siteBranch'],
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      canAccessPageBranching: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permission: ['createFromTemplate', 'siteBranch'],
    siteModes: {design: true, view: true},
    authorizations: {'SiteBranch/createFromTemplate': true},
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      isInPageBuildMode: true,
      canAccessPageBranching: true,
      hasUnrestrictedLocaleAccess: true,
    },
    userTypes: {
      authorizedInDesigner: true,
    },
    localization: ONLY_ON_PRIMARY_LOCALE,
    permission: ['createFromTemplate', 'siteBranch'],
    authorizations: {'SiteBranch/createFromTemplate': true},
  },
] as const);

export const canCreateStandaloneBranch = defineAbilityConfig([
  {abilities: {canCreateStandaloneBranchWithoutTemplate: true}},
  {abilities: {canCreateStandaloneBranchWithTemplate: true}},
] as const);

export const canCreateFolder = defineAbilityConfig({
  abilities: {
    hasUnrestrictedLocaleAccess: true,
  },
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  localization: ONLY_ON_PRIMARY_LOCALE,
  permission: ['design', 'site'],
  siteModes: {design: true},
  workflows: {
    canvas: true,
  },
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
} as const);

export const canCreateTemplatePage = defineAbilityConfig({
  abilities: {
    hasUnrestrictedLocaleAccess: true,
  },
  userTypes: {
    authorizedInDesigner: true,
  },
  localization: ONLY_ON_PRIMARY_LOCALE,
  permissions: [
    ['manageTemplates', 'sitePage'],
    ['design', 'site'],
  ],
  siteModes: {design: true},
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
} as const);

export const canDeleteTemplatePage = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
  },
  permission: ['manageTemplates', 'sitePage'],
  siteModes: {design: true},
} as const);

export const canModifyPage = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
    permission: ['modify', 'sitePage'],
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    authorizations: {'SitePage/modify': true},
  },
] as const);

/**
 * Whether the user can delete a static page.
 * Requires page modify permission AND unrestricted locale access
 * (deleting a page destroys all locale variants).
 */
export const canDeletePage = defineAbilityConfig([
  {
    abilities: {
      canAccessStaticPageContentFgac: false,
      hasUnrestrictedLocaleAccess: true,
    },
    permission: ['modify', 'sitePage'],
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      hasUnrestrictedLocaleAccess: true,
    },
    authorizations: {'SitePage/modify': true},
  },
] as const);

export const canEditStaticPageContent = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      canEditLocaleContent: true,
    },
    pageScope: ['main'],
    authorizations: {'SitePage/editContent': true},
    branchMergeLocked: false,
  },
  {
    abilities: {canEditBranchContent: true},
    pageScope: ['pageBranch', 'siteBranch'],
  },
] as const);

/**
 * Checks page-level SitePage/editContent authorization only, without the
 * canEditLocaleContent gate that canEditStaticPageContent includes.
 * Used to distinguish page-level restrictions from locale-level restrictions
 * in the pages panel lock indicators.
 */
export const canEditPageLevelContent = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    authorizations: {'SitePage/editContent': true},
  },
] as const);

/**
 * Release (site-branch) counterpart of canEditPageLevelContent for the pages
 * panel row's restricted state, which drives the lock icon and also the row's
 * drag handle and page-settings icon. A Release page is edited in a branch, so
 * only the page's own restriction matters, not the role's main-site grant.
 *
 * Kept separate on purpose: publishing composes canEditPageLevelContent as its
 * PSA gate, and that gate must keep requiring SitePage/editContent.
 *
 * Only the fine-grained-access clause is limited to Release pages. The FGAC-off
 * clause is unscoped on purpose, as in canEditPageLevelContent: without
 * fine-grained access nothing is locked.
 */
export const canEditReleasePageLevelContent = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    pageScope: ['siteBranch'],
    authorizations: {'SitePage/editContentInBranch': true},
  },
] as const);

/**
 * Checks branch-level SiteBranch/editContent authorization only, without the
 * canEditLocaleContent gate that canEditBranchContent includes.
 * Used to distinguish branch-level restrictions from locale/page restrictions
 * in the pages panel lock indicators.
 */
export const canEditBranchLevelContent = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    authorizations: {'SiteBranch/editContent': true},
  },
] as const);

export const canEditBranchContent = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      canEditLocaleContent: true,
    },
    pageScope: ['pageBranch'],
    authorizations: {'SiteBranch/editContent': true},
    branchMergeLocked: false,
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      canEditLocaleContent: true,
    },
    pageScope: ['main'],
    authorizations: {
      'SiteBranch/editContent': true,
      'SitePage/editContent': true,
    },
    branchMergeLocked: false,
  },
  {
    abilities: {
      canAccessStaticPageContentFgac: true,
      canEditLocaleContent: true,
    },
    pageScope: ['siteBranch'],
    authorizations: {
      'SiteBranch/editContent': true,
      'SitePage/editContentInBranch': true,
    },
    branchMergeLocked: false,
  },
] as const);

/**
 * Unified content-editing ability that resolves internally based on whether the
 * current page is a legacy page branch, site branch, or main page.
 *
 * This replaces the manual `isPageBranch ? canEditBranchContent : canEditStaticPageContent`
 * ternary that was previously repeated across 20+ call sites.
 */
export const canEditContent = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
  },
  {
    abilities: {canEditStaticPageContent: true},
    pageScope: ['main', 'siteBranch'],
  },
  {
    abilities: {canEditBranchContent: true},
    pageScope: ['pageBranch'],
  },
] as const);

export const canManageBranch = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
    permission: ['manage', 'siteBranch'],
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    authorizations: {'SiteBranch/manage': true},
  },
] as const);

export const canCreateBranchFromPage = defineAbilityConfig([
  {
    abilities: {canAccessStaticPageContentFgac: false},
    permission: ['createBranch', 'sitePage'],
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    authorizations: {'SitePage/createBranch': true},
  },
] as const);

export const canReadPageSettings = defineAbilityConfig([
  {
    userTypes: {
      unauthorized: true,
    },
    siteModes: {design: true, edit: true},
    permission: ['design', 'site'],
  },
  {
    abilities: {canAccessStaticPageContentFgac: false},
    userTypes: {
      authorizedInDesigner: true,
    },
    permissions: [
      ['read', 'sitePageSettings'],
      ['design', 'site'],
    ],
    siteModes: {design: true, edit: true},
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    userTypes: {
      authorizedInDesigner: true,
    },
    permissions: [
      ['read', 'sitePageSettings'],
      ['design', 'site'],
    ],
    siteModes: {design: true, edit: true},
    optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
    authorizations: {'SitePageSettings/read': true},
  },
] as const);

export const canReadBranchPageSettings = defineAbilityConfig([
  ...canReadPageSettings,
  {
    abilities: {canAccessStaticPageContentFgac: false},
    userTypes: {
      authorizedInDesigner: true,
    },
    branches: {
      branch: true,
    },
    permissions: [
      ['read', 'sitePageSettings'],
      ['design', 'siteBranch'],
    ],
    siteModes: {design: true, edit: true},
  },
  {
    abilities: {canAccessStaticPageContentFgac: true},
    userTypes: {
      authorizedInDesigner: true,
    },
    branches: {
      branch: true,
    },
    permissions: [
      ['read', 'sitePageSettings'],
      ['design', 'siteBranch'],
    ],
    siteModes: {design: true, edit: true},
    authorizations: {
      'SitePageSettings/read': true,
      'SiteBranch/design': true,
    },
  },
] as const);
